import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { api, type EntityListItem } from "@/api.js"
import { Async } from "@/components/Async.js"
import { Headline } from "@/components/Declared.js"
import { Flag } from "@/components/Flag.js"
import { PageHeader, Stat, StatRow } from "@/components/Page.js"
import { DecisionBadge, DegradedFlag } from "@/components/Status.js"
import { Badge } from "@/components/ui/badge.js"
import { Button } from "@/components/ui/button.js"
import { Input } from "@/components/ui/input.js"
import { LabeledSelectTrigger, Select, SelectContent, SelectItem } from "@/components/ui/select.js"
import { Table, Td, Th, Tr } from "@/components/ui/table.js"
import { useQuestions } from "@/questions.js"
import { formatDate } from "./Entity.js"

/**
 * The known base, browsed: every taxpayer some source holds anything about,
 * with what the core owns about each — how often it was asked, what the last
 * answer was — and each source's one word for it, in the source's own terms.
 *
 * **The name is shown, and since 2026-09-08 it is searchable too** — the
 * product owner's decision, taken knowing that for 57% of the base the
 * registered name is a person's own. What the box matches is a fragment of an
 * identifier or of a name, and the two are answered in different places: the
 * identifier here, the name by the source that holds it. Nothing about the
 * address, telephone number or email address in the same row is searched, and
 * the list is still keyed and paged by identifier. The row is the link to the
 * entity screen.
 *
 * The term lives in the URL, so the sidebar can navigate here with one and a
 * result somebody found can be pasted into a ticket.
 */
export function EntitiesScreen() {
  const [params, setParams] = useSearchParams()
  const searched = params.get("q") ?? ""
  const [text, setText] = useState(searched)
  const [filters, setFilters] = useState({ decision: "", override: "" })

  /*
   * The sidebar arrives with a term already typed, so the box has to follow the
   * URL — but only when the URL changed under it. Following its own writes
   * back would drop a keystroke landing between the debounce firing and this
   * effect running: the box would revert to the term that was just published
   * while somebody was still typing the next letter.
   */
  const published = useRef(searched)
  useEffect(() => {
    if (searched === published.current) return
    published.current = searched
    setText(searched)
  }, [searched])

  /*
   * Typing filters, but each keystroke fans out to every source and then asks
   * each of them for a word about every row that comes back — so what is typed
   * reaches the URL, and the query, a beat later. Anything under a keystroke
   * apart would be the same request storm with a delay in front of it.
   */
  useEffect(() => {
    if (text === searched) return
    const timer = setTimeout(() => {
      published.current = text.trim() === "" ? "" : text
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous)
          if (text.trim() === "") next.delete("q")
          else next.set("q", text)
          return next
        },
        { replace: true },
      )
    }, 250)
    return () => clearTimeout(timer)
  }, [text, searched, setParams])

  const query = {
    ...(searched.trim() === "" ? {} : { q: searched.trim() }),
    ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== "")),
  }

  const summary = useQuery({ queryKey: ["entities-summary"], queryFn: api.entitySummary })
  // Keyset pages, appended: the base grows at the top while somebody reads it.
  const state = useInfiniteQuery({
    queryKey: ["entities", query],
    queryFn: ({ pageParam }) => api.entities(query, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })
  const items = state.data?.pages.flatMap((page) => page.items) ?? []

  return (
    <div className="space-y-7">
      <PageHeader title="Podmioty" />

      <StatRow>
        <Stat
          label="Znane"
          value={summary.data?.known ?? "—"}
          hint="Podatnicy, o których jakieś źródło coś u nas trzyma."
        />
        <Stat
          label="Pytane w 30 dni"
          value={summary.data?.askedLast30Days ?? "—"}
          hint="Odrębne podmioty, o które padła decyzja w ostatnich 30 dniach."
        />
        <Stat
          label="Ostatnio przedpłata"
          value={summary.data?.lastPrepayment ?? "—"}
          tone={(summary.data?.lastPrepayment ?? 0) > 0 ? "prepay" : "neutral"}
          hint="Podmioty, których najnowsza decyzja to przedpłata."
        />
        <Stat
          label="Z odstępstwem"
          value={summary.data?.withActiveOverride ?? "—"}
          tone={(summary.data?.withActiveOverride ?? 0) > 0 ? "critical" : "neutral"}
          hint="Podmioty, dla których człowiek podstawia teraz odpowiedź."
        />
      </StatRow>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            className="w-72 text-[13px]"
            value={text}
            placeholder="NIP, REGON, KRS lub nazwa…"
            aria-label="Szukaj podmiotu"
            onChange={(event) => setText(event.target.value)}
          />

          <Select
            value={filters.decision === "" ? "any" : filters.decision}
            onValueChange={(value) =>
              setFilters({ ...filters, decision: value === "any" ? "" : value })
            }
          >
            <LabeledSelectTrigger label="Ostatnia decyzja" />
            <SelectContent>
              <SelectItem value="any">dowolna</SelectItem>
              <SelectItem value="ALLOW">odroczenie</SelectItem>
              <SelectItem value="PREPAYMENT">przedpłata</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.override === "" ? "any" : filters.override}
            onValueChange={(value) =>
              setFilters({ ...filters, override: value === "any" ? "" : value })
            }
          >
            <LabeledSelectTrigger label="Odstępstwo" />
            <SelectContent>
              <SelectItem value="any">dowolne</SelectItem>
              <SelectItem value="true">tylko z aktywnym</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Async state={state}>
          {() => (
            <div className="space-y-4">
              <EntityTable entities={items} filtered={Object.keys(query).length > 0} />
              {state.hasNextPage && (
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    onClick={() => void state.fetchNextPage()}
                    disabled={state.isFetchingNextPage}
                  >
                    {state.isFetchingNextPage ? "Wczytywanie…" : "Pokaż więcej"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </Async>
      </div>
    </div>
  )
}

function EntityTable({
  entities,
  filtered,
}: {
  entities: readonly EntityListItem[]
  filtered: boolean
}) {
  const navigate = useNavigate()
  const { titleOf } = useQuestions()

  if (entities.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {filtered
          ? "Żaden znany podmiot nie pasuje."
          : "Nie znamy jeszcze żadnego podmiotu — pierwsza decyzja go zapisze."}
      </p>
    )
  }

  return (
    <Table>
      <thead>
        <tr>
          <Th>Podmiot</Th>
          <Th>Nazwa</Th>
          <Th>Źródła</Th>
          <Th className="text-right">Decyzje</Th>
          <Th>Ostatnia decyzja</Th>
          <Th>Znany od</Th>
        </tr>
      </thead>
      <tbody>
        {entities.map((entity) => (
          // The row is the link: anyone reading this table is hunting one
          // entity. The identifier stays a real link for the keyboard.
          <Tr
            key={`${entity.country}${entity.taxId}`}
            className="cursor-pointer"
            onClick={() => void navigate(`/entities/${entity.country}/${entity.taxId}`)}
          >
            <Td className="whitespace-nowrap font-mono text-[13px] font-medium">
              <span className="inline-flex items-center gap-2.5">
                <Link
                  className="inline-flex items-center gap-2 text-foreground no-underline hover:text-primary"
                  to={`/entities/${entity.country}/${entity.taxId}`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <Flag code={entity.country} />
                  {entity.country}
                  {entity.taxId}
                </Link>
                {/*
                 * Overrides are a filter and a figure, not a column — except
                 * one with no end, which nothing will ever lapse: it is the
                 * override somebody has to review, so its row says so.
                 */}
                {entity.openEndedOverrides > 0 && (
                  <Badge
                    tone="review"
                    glyph="∞"
                    className="font-sans font-medium"
                    title="Odstępstwo bez terminu — obowiązuje, dopóki ktoś go nie odwoła."
                  >
                    bezterminowe odstępstwo
                  </Badge>
                )}
              </span>
            </Td>
            <Td className="max-w-[22rem]">
              {entity.name === null ? (
                <span className="text-[13px] text-muted-foreground">—</span>
              ) : (
                <span className="block truncate" title={entity.name}>
                  {entity.name}
                </span>
              )}
            </Td>
            <Td>
              {entity.sources.length === 0 ? (
                <span className="text-[13px] text-muted-foreground">—</span>
              ) : (
                <span className="flex flex-wrap gap-1.5">
                  {entity.sources.map((source) => (
                    <span key={source.providerId} title={source.title}>
                      <Headline headline={source.headline} />
                    </span>
                  ))}
                </span>
              )}
            </Td>
            <Td className="text-right tabular-nums">{entity.decisionCount}</Td>
            <Td className="whitespace-nowrap">
              {entity.lastDecision === null ? (
                <span className="text-[13px] text-muted-foreground">nie pytany</span>
              ) : (
                <span
                  className="inline-flex items-center gap-3"
                  title={titleOf(entity.lastDecision.question)}
                >
                  <DecisionBadge decision={entity.lastDecision.decision} />
                  <DegradedFlag degraded={entity.lastDecision.degraded} />
                  <span className="font-mono text-[12.5px] text-muted-foreground">
                    {formatDate(entity.lastDecision.evaluatedAt).slice(0, 10)}
                  </span>
                </span>
              )}
            </Td>
            <Td className="whitespace-nowrap font-mono text-[13px] text-muted-foreground">
              {formatDate(entity.firstSeenAt).slice(0, 10)}
            </Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  )
}
